import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    CheckCircle,
    Download,
    KeyRound,
    Loader2,
    RefreshCw,
    RotateCw,
    XCircle,
} from 'lucide-react';

import userMetadataApi from '../../api/userMetadataApi';
import { AdminBusinessMetadata } from '../../api/types';
import { adminDataApi } from '../../api/adminDataApi';
import vendorApi from '../../api/vendorApi';
import { useAdminContext } from './AdminContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import DataExportModal from './components/DataExportModal';
import RenormalizeModal, { RenormalizeParams } from './components/RenormalizeModal';
import AdminBillingPlansModal from './components/AdminBillingPlansModal';
import AdminAuditTrailPage from './AdminAuditTrailPage';
import AdminDebugConsole from './AdminDebugConsole';
import AdminConfigPage from './AdminConfigPage';
import AdminBillingPage from './AdminBillingPage';
import SignalPage from '../signals/SignalPage';
import DashboardV2 from '../dashboard-v2/DashboardV2';
import MarginV2 from '../margin/MarginV2';
import DocumentsPage from '../documents/DocumentsPage';
import LedgerPage from '../ledger';
import HistoryPage from '../history/HistoryPage';

const businessTabs = [
    { id: 'summary', label: 'Summary' },
    { id: 'home', label: 'Home' },
    { id: 'cash', label: 'Cash' },
    { id: 'margin', label: 'Margin' },
    { id: 'drive', label: 'Drive' },
    { id: 'ledger', label: 'Ledger' },
    { id: 'history', label: 'History' },
];

const adminSections = new Set(['audit', 'debug', 'config', 'billing', 'support']);

const getBusinessId = (business?: AdminBusinessMetadata | null) => business?.business_id || business?.user_id || '';
const getBusinessName = (business?: AdminBusinessMetadata | null) =>
    business?.legal_name ||
    business?.dba_name ||
    business?.onboarding?.qbo_metadata?.company_name ||
    business?.user_email ||
    'Unknown business';

const getOwnerName = (business?: AdminBusinessMetadata | null) => {
    const name = `${business?.first_name || ''} ${business?.last_name || ''}`.trim();
    return name || 'Unknown owner';
};

const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    });
};

const DetailCard: React.FC<{ title: string; children: React.ReactNode; action?: React.ReactNode }> = ({ title, children, action }) => (
    <section className="rounded-xl border border-gray-300 bg-white p-6">
        <div className="mb-6 flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {action}
        </div>
        {children}
    </section>
);

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <div>
        <div className="text-sm font-medium text-gray-500">{label}</div>
        <div className="mt-1 text-sm font-medium text-gray-900">{value || '-'}</div>
    </div>
);

const AdminBusinessDetailPage: React.FC = () => {
    const { businessId = '', section } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setSelectedBusinessId } = useAdminContext();
    const { setActiveBusinessId } = useWorkspace();
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [renormalizeModalOpen, setRenormalizeModalOpen] = useState(false);
    const [billingModalOpen, setBillingModalOpen] = useState(false);
    const [generatedConnectorToken, setGeneratedConnectorToken] = useState<{ businessId: string; businessName: string; token: string } | null>(null);

    useEffect(() => {
        if (businessId) {
            setSelectedBusinessId(businessId);
            setActiveBusinessId(businessId);
        }
    }, [businessId, setActiveBusinessId, setSelectedBusinessId]);

    const businessesQuery = useQuery({
        queryKey: ['allBusinesses'],
        queryFn: userMetadataApi.getAllBusinesses,
    });

    const opsQuery = useQuery({
        queryKey: ['admin-ops-status', businessId, 7, true],
        queryFn: () => adminDataApi.getOpsStatus({
            business_id: businessId,
            days: 7,
            include_idle: true,
            per_business_job_limit: 5,
        }),
        enabled: Boolean(businessId),
    });

    const business = useMemo(() => {
        return businessesQuery.data?.businesses.find((item) => getBusinessId(item) === businessId) || null;
    }, [businessId, businessesQuery.data]);

    const opsItem = opsQuery.data?.items?.[0];
    const activeProductTab = businessTabs.some((tab) => tab.id === section) ? section || 'summary' : 'summary';
    const isAdminSection = section ? adminSections.has(section) : false;
    const businessName = getBusinessName(business);
    const linkedStripe = Boolean(business?.onboarding?.connections?.linked_stripe);
    const linkedQbo = Boolean(business?.onboarding?.connections?.linked_qbo);

    const qboSyncMutation = useMutation({
        mutationFn: () => adminDataApi.syncQbo({ business_id: businessId, type: 'all' }),
        onSuccess: (result) => {
            alert(result.message || 'QBO sync triggered successfully');
            queryClient.invalidateQueries({ queryKey: ['admin-ops-status'] });
        },
        onError: () => alert('Failed to trigger QBO sync'),
    });

    const renormalizeMutation = useMutation({
        mutationFn: (params: RenormalizeParams) => vendorApi.renormalizeVendors({ ...params, businessId }),
        onSuccess: () => {
            alert('Renormalization triggered successfully');
            setRenormalizeModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['admin-ops-status'] });
        },
        onError: () => alert('Failed to trigger renormalization'),
    });

    const qbDesktopTokenMutation = useMutation({
        mutationFn: () => adminDataApi.createQbDesktopConnectorToken(businessId),
        onSuccess: (result) => {
            setGeneratedConnectorToken({
                businessId: result.business_id,
                businessName,
                token: result.token,
            });
        },
        onError: (error: any) => {
            const detail = error?.response?.data?.detail;
            alert(typeof detail === 'string' ? detail : 'Failed to generate connector token');
        },
    });

    if (businessesQuery.isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
        );
    }

    if (!business) {
        return (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
                Business not found.
            </div>
        );
    }

    const renderProductTab = () => {
        if (activeProductTab !== 'summary') {
            const pageByTab: Record<string, React.ReactNode> = {
                home: <SignalPage />,
                cash: <DashboardV2 />,
                margin: <MarginV2 />,
                drive: <DocumentsPage />,
                ledger: <LedgerPage />,
                history: <HistoryPage />,
            };

            return (
                <div className="h-[calc(100dvh-220px)] min-h-[640px] overflow-hidden">
                    {pageByTab[activeProductTab] || <SignalPage />}
                </div>
            );
        }

        return (
            <>
                <div className="grid gap-4 md:grid-cols-4">
                    <MetricCard
                        label="Active work"
                        value={opsItem?.active_work_count ?? 0}
                        hint={`Last updated ${formatDate(opsItem?.last_activity_at)}`}
                    />
                    <MetricCard
                        label="Pending AI"
                        value={opsItem?.vendor_resolution?.pending_ai_count ?? 0}
                        hint={`${opsItem?.vendor_resolution?.needs_review_count ?? 0} need review`}
                    />
                    <MetricCard
                        label="QBO Mapping"
                        value={opsItem?.qbo_account_mapping?.status || (linkedQbo ? 'Connected' : 'Not connected')}
                        hint={`Last updated ${formatDate(opsItem?.qbo_account_mapping?.updated_at)}`}
                    />
                    <MetricCard
                        label="Connectors"
                        value={(linkedStripe ? 1 : 0) + (linkedQbo ? 1 : 0)}
                        hint={`${linkedStripe ? 'Stripe' : 'No Stripe'} / ${linkedQbo ? 'QBO' : 'No QBO'}`}
                    />
                </div>

                <div className="grid gap-6 xl:grid-cols-2">
                    <DetailCard title="Personal Details">
                        <div className="grid gap-x-10 gap-y-8 md:grid-cols-2">
                            <Field label="First Name" value={business.first_name} />
                            <Field label="Last Name" value={business.last_name} />
                            <Field label="Email" value={business.user_email} />
                            <Field label="Phone Number" value="-" />
                            <Field label="User Name" value={business.user_email} />
                            <Field label="Password" value="**********" />
                        </div>
                    </DetailCard>

                    <ActionsCard
                        business={business}
                        onExport={() => setExportModalOpen(true)}
                        onBilling={() => setBillingModalOpen(true)}
                        onSyncQbo={() => qboSyncMutation.mutate()}
                        onRenormalize={() => setRenormalizeModalOpen(true)}
                        onToken={() => qbDesktopTokenMutation.mutate()}
                        busy={qboSyncMutation.isPending || qbDesktopTokenMutation.isPending}
                    />

                    <DetailCard
                        title="Business Details"
                        action={<RefreshCw className="h-5 w-5 text-gray-600" />}
                    >
                        <div className="grid gap-x-10 gap-y-8 md:grid-cols-2">
                            <Field label="Name" value={businessName} />
                            <Field label="Entity name" value={business.legal_name || businessName} />
                            <Field label="Date Formed" value={formatDate(business.onboarding?.qbo_metadata?.connected_at)} />
                            <Field label="Address" value="-" />
                            <Field label="Business ID" value={<span className="font-mono text-xs">{businessId}</span>} />
                            <Field label="Workspace ID" value={<span className="font-mono text-xs">{business.workspace_id || '-'}</span>} />
                        </div>
                    </DetailCard>

                    <DetailCard title="Connection Details">
                        <div className="grid gap-x-10 gap-y-8 md:grid-cols-2">
                            <Field label="Stripe" value={<ConnectionLabel connected={linkedStripe} />} />
                            <Field label="QBO" value={<ConnectionLabel connected={linkedQbo} />} />
                            <Field label="QBO Realm" value={business.onboarding?.qbo_metadata?.realm_id || '-'} />
                            <Field label="QBO Source" value={business.onboarding?.qbo_metadata?.source || business.onboarding?.qbo_metadata?.provider || 'QuickBooks Online'} />
                            <Field label="Last QBO Sync" value={formatDate(business.onboarding?.qbo_metadata?.last_sync_at || business.onboarding?.connections?.qbo_last_sync)} />
                            <Field label="Connector Status" value={business.onboarding?.qbo_metadata?.connector_status || '-'} />
                        </div>
                    </DetailCard>
                </div>
            </>
        );
    };

    const renderAdminSection = () => {
        if (section === 'audit') return <AdminAuditTrailPage />;
        if (section === 'debug') return <AdminDebugConsole />;
        if (section === 'config') return <AdminConfigPage />;
        if (section === 'billing') return <AdminBillingPage />;
        return (
            <div className="rounded-xl border border-gray-300 bg-white p-6">
                <h2 className="text-lg font-semibold text-gray-900">Support</h2>
                <div className="mt-6 grid gap-4 md:grid-cols-3">
                    <MetricCard label="Owner" value={getOwnerName(business)} />
                    <MetricCard label="Email" value={business.user_email || '-'} />
                    <MetricCard label="Status" value={business.status || 'active'} />
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-full bg-gray-50 px-6 py-6">
            <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/admin')}
                            className="rounded-md p-1 text-gray-700 hover:bg-gray-100"
                            aria-label="Back to admin home"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                        <h1 className="text-2xl font-semibold text-gray-900">{businessName}</h1>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 pl-8 text-sm text-gray-500">
                        <span>{getOwnerName(business)}</span>
                        <span>·</span>
                        <span>{business.user_email}</span>
                        <span>·</span>
                        <span className="font-mono text-xs">{businessId}</span>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <select className="h-11 rounded-lg border border-gray-200 bg-white px-4 text-sm text-gray-900 shadow-sm">
                        <option>This week</option>
                        <option>Last 7 days</option>
                        <option>Last 30 days</option>
                    </select>
                    <button
                        onClick={() => {
                            queryClient.invalidateQueries({ queryKey: ['allBusinesses'] });
                            queryClient.invalidateQueries({ queryKey: ['admin-ops-status'] });
                        }}
                        className="h-11 rounded-lg bg-gray-900 px-6 text-sm font-semibold text-white hover:bg-gray-800"
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {!isAdminSection && (
                <div className="mb-8 flex items-center gap-7">
                    {businessTabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => navigate(tab.id === 'summary' ? `/admin/business/${businessId}` : `/admin/business/${businessId}/${tab.id}`)}
                            className={`border-b-2 pb-3 text-sm font-semibold transition-colors ${activeProductTab === tab.id
                                ? 'border-indigo-500 text-gray-900'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="space-y-6">
                {isAdminSection ? renderAdminSection() : renderProductTab()}
            </div>

            <DataExportModal
                isOpen={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                businessId={businessId}
                businessName={businessName}
            />

            <RenormalizeModal
                isOpen={renormalizeModalOpen}
                onClose={() => setRenormalizeModalOpen(false)}
                onConfirm={(params) => renormalizeMutation.mutate(params)}
                isPending={renormalizeMutation.isPending}
                businessName={businessName}
            />

            {business.workspace_id && (
                <AdminBillingPlansModal
                    isOpen={billingModalOpen}
                    workspaceId={business.workspace_id}
                    businessName={businessName}
                    onClose={() => setBillingModalOpen(false)}
                />
            )}

            {generatedConnectorToken && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
                        <h3 className="text-lg font-semibold text-gray-900">QuickBooks Desktop connector token</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Token for {generatedConnectorToken.businessName}. It is shown once; paste it into the Windows connector API token field.
                        </p>
                        <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-sm text-gray-900 break-all">
                            {generatedConnectorToken.token}
                        </div>
                        <div className="mt-5 flex justify-end">
                            <button
                                onClick={() => setGeneratedConnectorToken(null)}
                                className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const MetricCard: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
    <div className="rounded-xl border border-gray-300 bg-white p-5">
        <div className="text-sm font-medium text-gray-500">{label}</div>
        <div className="mt-3 text-xl font-semibold text-gray-900">{value}</div>
        {hint && <div className="mt-4 text-sm text-gray-500">{hint}</div>}
    </div>
);

const ConnectionLabel: React.FC<{ connected: boolean }> = ({ connected }) => (
    <span className="inline-flex items-center gap-2">
        {connected ? <CheckCircle className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-gray-400" />}
        {connected ? 'Connected' : 'Not connected'}
    </span>
);

const ActionsCard: React.FC<{
    business: AdminBusinessMetadata;
    onExport: () => void;
    onBilling: () => void;
    onSyncQbo: () => void;
    onRenormalize: () => void;
    onToken: () => void;
    busy: boolean;
}> = ({ business, onExport, onBilling, onSyncQbo, onRenormalize, onToken, busy }) => (
    <DetailCard title="Actions">
        <div className="grid gap-3 sm:grid-cols-2">
            <button onClick={onSyncQbo} className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
                Sync QBO
            </button>
            <button onClick={onRenormalize} className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                <RotateCw className="h-4 w-4" />
                Renormalize
            </button>
            <button onClick={onExport} className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                <Download className="h-4 w-4" />
                Export
            </button>
            <button
                onClick={onBilling}
                disabled={!business.workspace_id}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
            >
                Billing
            </button>
            <button onClick={onToken} className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:col-span-2">
                <KeyRound className={`h-4 w-4 ${busy ? 'animate-pulse' : ''}`} />
                Connector token
            </button>
        </div>
    </DetailCard>
);

export default AdminBusinessDetailPage;
